'use client';

import React, { useState } from 'react';
import { Info } from 'lucide-react';
import { mockAssessment, EssaySegment } from '@/data/mockAssessment';

const errorTypeConfig = {
  grammar: {
    label: 'Grammar',
    strikeBg: 'bg-danger/10',
    strikeColor: 'text-danger',
    correctionColor: 'text-success',
    dotColor: 'bg-danger',
    borderColor: 'border-danger/30',
    explBg: 'bg-danger-bg border-danger-border',
  },
  vocabulary: {
    label: 'Vocabulary',
    strikeBg: 'bg-warning/10',
    strikeColor: 'text-warning',
    correctionColor: 'text-success',
    dotColor: 'bg-warning',
    borderColor: 'border-warning/30',
    explBg: 'bg-warning-bg border-warning-border',
  },
  cohesion: {
    label: 'Cohesion',
    strikeBg: 'bg-info/10',
    strikeColor: 'text-info',
    correctionColor: 'text-success',
    dotColor: 'bg-info',
    borderColor: 'border-info/30',
    explBg: 'bg-info-bg border-info-border',
  },
  punctuation: {
    label: 'Punctuation',
    strikeBg: 'bg-secondary',
    strikeColor: 'text-secondary-foreground',
    correctionColor: 'text-success',
    dotColor: 'bg-muted-foreground',
    borderColor: 'border-border',
    explBg: 'bg-muted border-border',
  },
};

interface SegmentPopoverProps {
  segment: EssaySegment;
  isOpen: boolean;
  onToggle: () => void;
}

function AnnotatedSegment({ segment, isOpen, onToggle }: SegmentPopoverProps) {
  if (!segment.isError || !segment.errorType) {
    return <span>{segment.text}</span>;
  }

  const cfg = errorTypeConfig[segment.errorType];

  return (
    <span className="relative inline">
      <button
        onClick={onToggle}
        className={`inline rounded px-0.5 cursor-pointer transition-all duration-150 ${cfg.strikeBg} ${cfg.strikeColor}`}
        style={{ textDecoration: 'line-through', textDecorationColor: 'currentColor' }}
        title={`${cfg.label} error — click for explanation`}
      >
        {segment.text}
      </button>
      <span className={`inline ml-1 font-semibold text-success`}>
        ({segment.correction})
      </span>
      {isOpen && segment.explanation && (
        <span
          className={`absolute left-0 top-full mt-1 z-20 w-64 p-3 rounded-lg border text-xs text-secondary-foreground leading-relaxed shadow-lg ${cfg.explBg} fade-in`}
          style={{ minWidth: '240px' }}
        >
          <span className={`font-bold ${cfg.strikeColor}`}>{cfg.label} Error: </span>
          {segment.explanation}
        </span>
      )}
    </span>
  );
}

export default function AnnotatedEssayTab() {
  const { annotatedEssayParagraphs } = mockAssessment;
  const [openSegment, setOpenSegment] = useState<string | null>(null);

  const allErrors = annotatedEssayParagraphs.flatMap((p) =>
    p.segments.filter((s) => s.isError)
  );

  const errorCounts = {
    grammar: allErrors.filter((e) => e.errorType === 'grammar').length,
    vocabulary: allErrors.filter((e) => e.errorType === 'vocabulary').length,
    cohesion: allErrors.filter((e) => e.errorType === 'cohesion').length,
    punctuation: allErrors.filter((e) => e.errorType === 'punctuation').length,
  };

  return (
    <div className="p-6">
      {/* Error Legend */}
      <div className="mb-5 p-4 rounded-xl border bg-muted/20">
        <div className="flex items-center gap-2 mb-3">
          <Info size={13} className="text-muted-foreground" />
          <p className="text-xs font-semibold text-foreground">Error Annotation Legend</p>
          <span className="ml-auto text-xs text-muted-foreground">Click any highlighted error to see the explanation</span>
        </div>
        <div className="flex flex-wrap gap-3">
          {(Object.entries(errorTypeConfig) as [keyof typeof errorTypeConfig, typeof errorTypeConfig[keyof typeof errorTypeConfig]][]).map(([type, cfg]) => (
            <div key={`legend-${type}`} className="flex items-center gap-2">
              <div className={`w-2.5 h-2.5 rounded-full ${cfg.dotColor}`} />
              <span className="text-xs text-muted-foreground font-medium">{cfg.label}</span>
              <span className="text-xs px-1.5 py-0.5 rounded-full bg-muted font-semibold text-foreground">
                {errorCounts[type as keyof typeof errorCounts]}
              </span>
            </div>
          ))}
          <div className="ml-auto flex items-center gap-1.5">
            <span className="text-xs text-muted-foreground">Total errors:</span>
            <span className="text-xs font-bold text-danger">{allErrors.length}</span>
          </div>
        </div>
      </div>

      {/* Annotated Essay */}
      <div className="p-5 rounded-xl border bg-muted/10">
        <div className="space-y-4">
          {annotatedEssayParagraphs.map((para) => (
            <p key={para.id} className="text-sm text-foreground leading-[1.9] essay-textarea relative">
              {para.segments.map((seg) => (
                <AnnotatedSegment
                  key={seg.id}
                  segment={seg}
                  isOpen={openSegment === seg.id}
                  onToggle={() => setOpenSegment(openSegment === seg.id ? null : seg.id)}
                />
              ))}
            </p>
          ))}
        </div>
      </div>

      {/* Error summary table */}
      <div className="mt-5">
        <h4 className="text-sm font-semibold text-foreground mb-3">Error Summary</h4>
        <div className="rounded-xl border overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/40 border-b">
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Error Type</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Original</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Correction</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider hidden lg:table-cell">Explanation</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {allErrors.map((err, i) => {
                const cfg = errorTypeConfig[err.errorType as keyof typeof errorTypeConfig];
                return (
                  <tr key={`err-row-${err.id}`} className="hover:bg-muted/30 transition-all duration-150">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <div className={`w-2 h-2 rounded-full ${cfg.dotColor}`} />
                        <span className={`text-xs font-semibold ${cfg.strikeColor}`}>{cfg.label}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-mono ${cfg.strikeColor}`} style={{ textDecoration: 'line-through' }}>
                        {err.text}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-semibold text-success font-mono">{err.correction}</span>
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">{err.explanation}</p>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}